import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Alert,
  Box,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Chip,
  CircularProgress,
  Grid,
  Card,
  CardContent,
  Collapse
} from '@mui/material';
import {
  Warning as WarningIcon,
  People as PeopleIcon,
  Schedule as ScheduleIcon,
  Security as SecurityIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon
} from '@mui/icons-material';
import { format } from 'date-fns';
import planApi from '../utils/planApi';

const PlanImpactDialog = ({ 
  open, 
  onClose, 
  planId, 
  planName = 'this plan',
  title = 'Plan Impact Analysis',
  warningMessage = null,
  showProceedButton = true,
  proceedButtonText = 'Proceed with Edit',
  onProceed = null
}) => {
  const [impact, setImpact] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showDetails, setShowDetails] = useState(false);

  const fetchPlanImpact = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const impactData = await planApi.getPlanImpact(planId);
      setImpact(impactData);
    } catch (err) {
      setError('Failed to load plan impact information');
      console.error('Error fetching plan impact:', err);
    } finally {
      setLoading(false);
    }
  }, [planId]);

  useEffect(() => {
    if (open && planId) {
      fetchPlanImpact();
    }
  }, [open, planId, fetchPlanImpact]);

  const formatDate = (dateString) => {
    try {
      return format(new Date(dateString), 'MMM dd, yyyy');
    } catch {
      return 'Unknown date';
    }
  };

  const getSeverityLevel = () => {
    if (!impact) return 'info';
    
    const { totalActiveUsers, pendingRegistrations } = impact.impact;
    
    if (totalActiveUsers > 10 || pendingRegistrations > 5) return 'error';
    if (totalActiveUsers > 0 || pendingRegistrations > 0) return 'warning';
    return 'info';
  };

  const getSeverityIcon = () => {
    const severity = getSeverityLevel();
    const iconProps = { sx: { fontSize: 20 } };
    
    switch (severity) {
      case 'error':
      case 'warning':
        return <WarningIcon color={severity} {...iconProps} />;
      case 'info':
      default:
        return <SecurityIcon color="info" {...iconProps} />;
    }
  };

  const getSummaryMessage = () => {
    if (!impact) return '';
    
    const { totalActiveUsers, pendingRegistrations, canSafelyEdit } = impact.impact;
    
    if (canSafelyEdit) {
      return `This plan has no active users or pending registrations. It can be safely edited without affecting anyone.`;
    }
    
    const userPart = totalActiveUsers > 0 
      ? `${totalActiveUsers} active user${totalActiveUsers !== 1 ? 's' : ''}` 
      : '';
    const regPart = pendingRegistrations > 0 
      ? `${pendingRegistrations} pending registration${pendingRegistrations !== 1 ? 's' : ''}` 
      : '';
    
    const parts = [userPart, regPart].filter(Boolean);
    const joinedParts = parts.join(' and ');
    
    return `This plan has ${joinedParts}.`;
  };

  const getProtectionMessage = () => {
    if (!impact || impact.impact.canSafelyEdit) return '';
    
    return "Don't worry - existing users are protected! They have 'plan snapshots' that preserve their original plan benefits exactly as they were when purchased. Your changes will only affect new users who select this plan.";
  };

  if (loading) {
    return (
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogContent sx={{ textAlign: 'center', py: 4 }}>
          <CircularProgress sx={{ mb: 2 }} />
          <Typography>Loading plan impact information...</Typography>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {getSeverityIcon()}
          <Typography variant="h6" component="span">
            {title}
          </Typography>
        </Box>
        <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 0.5 }}>
          {planName}
        </Typography>
      </DialogTitle>

      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {warningMessage && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            {warningMessage}
          </Alert>
        )}

        {impact && (
          <>
            {/* Summary Message */}
            <Alert 
              severity={getSeverityLevel()} 
              sx={{ mb: 2 }}
              icon={getSeverityIcon()}
            >
              <Typography variant="body1" sx={{ fontWeight: 500 }}>
                {getSummaryMessage()}
              </Typography>
              {!impact.impact.canSafelyEdit && (
                <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary' }}>
                  {getProtectionMessage()}
                </Typography>
              )}
            </Alert>

            {/* Impact Statistics */}
            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid size={{ xs: 12, sm: 4 }}>
                <Card variant="outlined">
                  <CardContent sx={{ textAlign: 'center', py: 2 }}>
                    <PeopleIcon color="primary" sx={{ fontSize: 32, mb: 1 }} />
                    <Typography variant="h4" color="primary">
                      {impact.impact.totalActiveUsers}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Active Users
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              
              <Grid size={{ xs: 12, sm: 4 }}>
                <Card variant="outlined">
                  <CardContent sx={{ textAlign: 'center', py: 2 }}>
                    <ScheduleIcon color="warning" sx={{ fontSize: 32, mb: 1 }} />
                    <Typography variant="h4" color="warning.main">
                      {impact.impact.pendingRegistrations}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Pending Registrations
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              
              <Grid size={{ xs: 12, sm: 4 }}>
                <Card variant="outlined">
                  <CardContent sx={{ textAlign: 'center', py: 2 }}>
                    <SecurityIcon color="info" sx={{ fontSize: 32, mb: 1 }} />
                    <Typography variant="h4" color="info.main">
                      {impact.impact.expiredUsersLast30Days}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Expired (30 days)
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

            {/* Plan Protection Status */}
            <Box sx={{ mb: 2 }}>
              <Card variant="outlined" sx={{ bgcolor: 'success.50', borderColor: 'success.200' }}>
                <CardContent sx={{ py: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <SecurityIcon color="success" />
                    <Typography variant="h6" color="success.dark">
                      Plan Snapshot Protection Active
                    </Typography>
                  </Box>
                  <Typography variant="body2" color="success.dark">
                    All existing users have protected plan snapshots that preserve their original benefits. 
                    Changes to this plan will not affect them - only new users will get the updated version.
                  </Typography>
                </CardContent>
              </Card>
            </Box>

            {/* Detailed User Information */}
            {impact.activeUsersPreview && impact.activeUsersPreview.length > 0 && (
              <Box>
                <Button
                  onClick={() => setShowDetails(!showDetails)}
                  startIcon={showDetails ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                  sx={{ mb: 1 }}
                >
                  {showDetails ? 'Hide' : 'Show'} Active Users Details
                </Button>
                
                <Collapse in={showDetails}>
                  <Card variant="outlined">
                    <CardContent sx={{ py: 1 }}>
                      <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                        Active Users Preview (showing first {impact.activeUsersPreview.length} users)
                      </Typography>
                      <List dense>
                        {impact.activeUsersPreview.map((user, index) => (
                          <ListItem key={user._id} sx={{ px: 0 }}>
                            <ListItemIcon sx={{ minWidth: 36 }}>
                              <PeopleIcon fontSize="small" />
                            </ListItemIcon>
                            <ListItemText
                              primary={`${user.firstName} ${user.lastName}`}
                              secondary={
                                <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
                                  <Chip 
                                    label={user.email} 
                                    size="small" 
                                    variant="outlined"
                                  />
                                  <Chip 
                                    label={`Expires: ${formatDate(user.membershipEndDate)}`} 
                                    size="small" 
                                    color="primary"
                                    variant="outlined"
                                  />
                                </Box>
                              }
                            />
                          </ListItem>
                        ))}
                      </List>
                      {impact.impact.totalActiveUsers > impact.activeUsersPreview.length && (
                        <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                          ... and {impact.impact.totalActiveUsers - impact.activeUsersPreview.length} more user(s)
                        </Typography>
                      )}
                    </CardContent>
                  </Card>
                </Collapse>
              </Box>
            )}
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose}>
          Cancel
        </Button>
        {showProceedButton && onProceed && (
          <Button 
            onClick={onProceed} 
            variant="contained"
            color={getSeverityLevel() === 'info' ? 'primary' : 'warning'}
          >
            {proceedButtonText}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default PlanImpactDialog;